import { Fragment, MutableRefObject, ReactNode } from "react";
import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import { CgArrowLeft } from "react-icons/cg";

import { IoCloseOutline } from "react-icons/io5";

import { BUTTONS } from "testIds";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  modalInitialFocusRef: MutableRefObject<null>;
  header?: string;
  panelClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  closeButton?: boolean;
  dark?: boolean;
}

const CloseButton = ({
  onClose,
  dark,
}: {
  onClose: () => void;
  dark?: boolean;
}) => {
  return (
    <>
      <button
        data-testid={BUTTONS.CLOSE_MODAL}
        type="button"
        onClick={onClose}
        className="hidden sm:block absolute top-1 sm:top-0 right-1 sm:right-0 bg-black hover:bg-gray-700 rounded-full border-2 border-transparent transition-all sm:translate-x-1/4 sm:-translate-y-1/4 cursor-pointer outline-none"
      >
        <IoCloseOutline color={dark ? "black" : "white"} size={32} />
      </button>
      <button
        data-testid={BUTTONS.CLOSE_MODAL}
        type="button"
        onClick={onClose}
        className="block sm:hidden mt-2 ml-4 cursor-pointer outline-none"
      >
        <CgArrowLeft color={dark ? "white" : "black"} size={24} />
      </button>
    </>
  );
};

export const Modal = ({
  isOpen,
  onClose,
  children,
  modalInitialFocusRef,
  header,
  panelClassName,
  headerClassName,
  bodyClassName,
  closeButton,
  dark,
}: ModalProps) => {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} initialFocus={modalInitialFocusRef}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-30 bg-black/25" aria-hidden="true" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="flex overflow-hidden sm:overflow-auto fixed inset-0 z-30 justify-center items-baseline p-0 lg:p-4">
            <div className="flex relative justify-center items-center sm:pt-0 w-full h-full sm:h-auto sm:min-h-full">
              <Dialog.Panel
                className={classNames(
                  "relative flex flex-col w-screen h-full sm:h-auto sm:w-auto max-w-full sm:max-w-none rounded-none sm:rounded-3xl shadow-sm",
                  dark ? "bg-black" : "bg-[#E5EEEF]",
                  panelClassName
                )}
              >
                {closeButton && !header && (
                  <div className="flex p-2 sm:p-0 shrink-0">
                    <CloseButton onClose={onClose} dark={dark} />
                  </div>
                )}
                {!!header && (
                  <div
                    className={classNames(
                      "flex shrink-0 z-10 justify-between items-center py-2 sm:py-4 px-4 md:px-6 text-2xl font-bold bg-gray-200",
                      headerClassName
                    )}
                  >
                    <div>{header}</div>
                    <CloseButton onClose={onClose} dark={dark} />
                  </div>
                )}
                <div
                  className={classNames(
                    "flex-1 overflow-y-auto overflow-x-hidden p-4 md:px-6",
                    bodyClassName
                  )}
                >
                  {children}
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};
